import { cn } from '@/lib/utils';
import type { OpportunityState } from '@tennis-scanner/shared';

// Maps each state in OPPORTUNITY_STATES to a color + dot + label. Keeping
// this consistent across pages means an operator scanning the dashboard
// vs the cockpit always sees the same visual language for "active" /
// "weakened" / etc.

const STATE_META: Record<OpportunityState, { label: string; color: string; dot: string }> = {
  // Operator-facing labels in Spanish. The internal state strings (detected,
  // active, weakened, etc.) come straight from the DB enum — we keep the
  // mapping centralized here so every page renders identically.
  detected: {
    label: 'NUEVA',
    color: 'border-info/40 bg-info/10 text-info',
    dot: 'bg-info',
  },
  active: {
    label: 'ACTIVA',
    color: 'border-positive/40 bg-positive/10 text-positive',
    dot: 'bg-positive',
  },
  weakened: {
    label: 'DEBILITADA',
    color: 'border-warning/40 bg-warning/10 text-warning',
    dot: 'bg-warning',
  },
  reappeared: {
    label: 'REAPARECIÓ',
    color: 'border-info/40 bg-info/10 text-info',
    dot: 'bg-info',
  },
  closed: {
    label: 'CERRADA',
    color: 'border-border bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground',
  },
  expired: {
    label: 'EXPIRÓ',
    color: 'border-border bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground',
  },
  executed: {
    label: 'EJECUTADA',
    color: 'border-ai-verdict/40 bg-ai-verdict/10 text-ai-verdict',
    dot: 'bg-ai-verdict',
  },
  passed: {
    label: 'DESCARTADA',
    color: 'border-border bg-muted text-muted-foreground',
    dot: 'bg-muted-foreground',
  },
  snoozed: {
    label: 'PAUSADA',
    color: 'border-warning/40 bg-warning/10 text-warning',
    dot: 'bg-warning',
  },
};

interface Props {
  state: OpportunityState;
  className?: string;
}

export function StateBadge({ state, className }: Props) {
  const meta = STATE_META[state];
  return (
    <span
      data-slot="state-badge"
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-mono tracking-wider',
        meta.color,
        className,
      )}
    >
      <span className={cn('size-1.5 rounded-full', meta.dot)} />
      {meta.label}
    </span>
  );
}
